/**
 * Country (ISO 3166-1 alpha-2) → primary IANA timezone.
 *
 * The FRONTEND twin of backend `app/core/country_timezones.py` — kept in sync so
 * the create form can validate a visit time in the on-site zone (e.g. "is this
 * Chicago time in the future?") before the ticket is saved. The BACKEND remains
 * authoritative (it re-resolves site → country → tenant → UTC on save); this is
 * for client-side validation/UX only. Multi-zone countries use their primary.
 */
const COUNTRY_TIMEZONE = {
    GB: 'Europe/London',
    IN: 'Asia/Kolkata',
    AE: 'Asia/Dubai',
    AU: 'Australia/Sydney',
    AT: 'Europe/Vienna',
    BH: 'Asia/Bahrain',
    BD: 'Asia/Dhaka',
    BE: 'Europe/Brussels',
    BR: 'America/Sao_Paulo',
    CA: 'America/Toronto',
    CN: 'Asia/Shanghai',
    DK: 'Europe/Copenhagen',
    EG: 'Africa/Cairo',
    FI: 'Europe/Helsinki',
    FR: 'Europe/Paris',
    DE: 'Europe/Berlin',
    GH: 'Africa/Accra',
    GR: 'Europe/Athens',
    HK: 'Asia/Hong_Kong',
    HU: 'Europe/Budapest',
    ID: 'Asia/Jakarta',
    IE: 'Europe/Dublin',
    IL: 'Asia/Jerusalem',
    IT: 'Europe/Rome',
    JP: 'Asia/Tokyo',
    JO: 'Asia/Amman',
    KE: 'Africa/Nairobi',
    KW: 'Asia/Kuwait',
    MY: 'Asia/Kuala_Lumpur',
    MX: 'America/Mexico_City',
    MA: 'Africa/Casablanca',
    NL: 'Europe/Amsterdam',
    NZ: 'Pacific/Auckland',
    NG: 'Africa/Lagos',
    NO: 'Europe/Oslo',
    OM: 'Asia/Muscat',
    PK: 'Asia/Karachi',
    PH: 'Asia/Manila',
    PL: 'Europe/Warsaw',
    PT: 'Europe/Lisbon',
    QA: 'Asia/Qatar',
    RO: 'Europe/Bucharest',
    SA: 'Asia/Riyadh',
    SG: 'Asia/Singapore',
    ZA: 'Africa/Johannesburg',
    KR: 'Asia/Seoul',
    ES: 'Europe/Madrid',
    LK: 'Asia/Colombo',
    SE: 'Europe/Stockholm',
    CH: 'Europe/Zurich',
    TW: 'Asia/Taipei',
    TZ: 'Africa/Dar_es_Salaam',
    TH: 'Asia/Bangkok',
    TR: 'Europe/Istanbul',
    UG: 'Africa/Kampala',
    US: 'America/New_York',
    VN: 'Asia/Ho_Chi_Minh',
    // Full ISO picker coverage (2026-08-14) — mirrors the backend map.
    AF: 'Asia/Kabul',
    AL: 'Europe/Tirane',
    DZ: 'Africa/Algiers',
    AD: 'Europe/Andorra',
    AO: 'Africa/Luanda',
    AG: 'America/Antigua',
    AR: 'America/Argentina/Buenos_Aires',
    AM: 'Asia/Yerevan',
    AZ: 'Asia/Baku',
    BS: 'America/Nassau',
    BB: 'America/Barbados',
    BY: 'Europe/Minsk',
    BZ: 'America/Belize',
    BJ: 'Africa/Porto-Novo',
    BT: 'Asia/Thimphu',
    BO: 'America/La_Paz',
    BA: 'Europe/Sarajevo',
    BW: 'Africa/Gaborone',
    BN: 'Asia/Brunei',
    BG: 'Europe/Sofia',
    BF: 'Africa/Ouagadougou',
    BI: 'Africa/Bujumbura',
    CV: 'Atlantic/Cape_Verde',
    KH: 'Asia/Phnom_Penh',
    CM: 'Africa/Douala',
    CF: 'Africa/Bangui',
    TD: 'Africa/Ndjamena',
    CL: 'America/Santiago',
    CO: 'America/Bogota',
    KM: 'Indian/Comoro',
    CG: 'Africa/Brazzaville',
    CD: 'Africa/Kinshasa',
    CR: 'America/Costa_Rica',
    CI: 'Africa/Abidjan',
    HR: 'Europe/Zagreb',
    CU: 'America/Havana',
    CY: 'Asia/Nicosia',
    CZ: 'Europe/Prague',
    DJ: 'Africa/Djibouti',
    DM: 'America/Dominica',
    DO: 'America/Santo_Domingo',
    EC: 'America/Guayaquil',
    SV: 'America/El_Salvador',
    GQ: 'Africa/Malabo',
    ER: 'Africa/Asmara',
    EE: 'Europe/Tallinn',
    SZ: 'Africa/Mbabane',
    ET: 'Africa/Addis_Ababa',
    FJ: 'Pacific/Fiji',
    GA: 'Africa/Libreville',
    GM: 'Africa/Banjul',
    GE: 'Asia/Tbilisi',
    GD: 'America/Grenada',
    GT: 'America/Guatemala',
    GN: 'Africa/Conakry',
    GW: 'Africa/Bissau',
    GY: 'America/Guyana',
    HT: 'America/Port-au-Prince',
    HN: 'America/Tegucigalpa',
    IS: 'Atlantic/Reykjavik',
    IR: 'Asia/Tehran',
    IQ: 'Asia/Baghdad',
    JM: 'America/Jamaica',
    KZ: 'Asia/Almaty',
    KI: 'Pacific/Tarawa',
    KG: 'Asia/Bishkek',
    LA: 'Asia/Vientiane',
    LV: 'Europe/Riga',
    LB: 'Asia/Beirut',
    LS: 'Africa/Maseru',
    LR: 'Africa/Monrovia',
    LY: 'Africa/Tripoli',
    LI: 'Europe/Vaduz',
    LT: 'Europe/Vilnius',
    LU: 'Europe/Luxembourg',
    MG: 'Indian/Antananarivo',
    MW: 'Africa/Blantyre',
    MV: 'Indian/Maldives',
    ML: 'Africa/Bamako',
    MT: 'Europe/Malta',
    MH: 'Pacific/Majuro',
    MR: 'Africa/Nouakchott',
    MU: 'Indian/Mauritius',
    FM: 'Pacific/Pohnpei',
    MD: 'Europe/Chisinau',
    MC: 'Europe/Monaco',
    MN: 'Asia/Ulaanbaatar',
    ME: 'Europe/Podgorica',
    MZ: 'Africa/Maputo',
    MM: 'Asia/Yangon',
    NA: 'Africa/Windhoek',
    NR: 'Pacific/Nauru',
    NP: 'Asia/Kathmandu',
    NI: 'America/Managua',
    NE: 'Africa/Niamey',
    KP: 'Asia/Pyongyang',
    MK: 'Europe/Skopje',
    PW: 'Pacific/Palau',
    PS: 'Asia/Hebron',
    PA: 'America/Panama',
    PG: 'Pacific/Port_Moresby',
    PY: 'America/Asuncion',
    PE: 'America/Lima',
    RU: 'Europe/Moscow',
    RW: 'Africa/Kigali',
    KN: 'America/St_Kitts',
    LC: 'America/St_Lucia',
    VC: 'America/St_Vincent',
    WS: 'Pacific/Apia',
    SM: 'Europe/San_Marino',
    ST: 'Africa/Sao_Tome',
    SN: 'Africa/Dakar',
    RS: 'Europe/Belgrade',
    SC: 'Indian/Mahe',
    SL: 'Africa/Freetown',
    SK: 'Europe/Bratislava',
    SI: 'Europe/Ljubljana',
    SB: 'Pacific/Guadalcanal',
    SO: 'Africa/Mogadishu',
    SS: 'Africa/Juba',
    SD: 'Africa/Khartoum',
    SR: 'America/Paramaribo',
    SY: 'Asia/Damascus',
    TJ: 'Asia/Dushanbe',
    TL: 'Asia/Dili',
    TG: 'Africa/Lome',
    TO: 'Pacific/Tongatapu',
    TT: 'America/Port_of_Spain',
    TN: 'Africa/Tunis',
    TM: 'Asia/Ashgabat',
    TV: 'Pacific/Funafuti',
    UA: 'Europe/Kyiv',
    UY: 'America/Montevideo',
    UZ: 'Asia/Tashkent',
    VU: 'Pacific/Efate',
    VA: 'Europe/Vatican',
    VE: 'America/Caracas',
    YE: 'Asia/Aden',
    ZM: 'Africa/Lusaka',
    ZW: 'Africa/Harare',
};
/** Primary IANA timezone for an ISO alpha-2 country code, or null if unknown. */
export function timezoneForCountry(code) {
    if (!code)
        return null;
    return COUNTRY_TIMEZONE[code.trim().toUpperCase()] ?? null;
}
